/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import config from 'linced-app/config/environment';
import moment from 'moment';
import {
  compact,
  filterBy,
  uniq,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';

class SapRefund extends EmberObject {
  @tracked
  refunds;

  @tracked
  refundsProcess;

  @computed('refundType', 'refundFrom', 'amountRefundable')
  get max() {
    if (isEmpty(this.refundFrom)) {
      return this.amountRefundable;
    }
    let max = 0;
    if (this.refundType === 'account') {
      max = this.get('refundFrom.balance');
    } else {
      max = this.get('refundFrom.credit');
    }
    if (max <= 0) {
      // Don't return a zero (or negative) amount, in order to avoid useless
      // "must be less than 0" validation errors.
      return this.amountRefundable;
    }
    return Math.min(this.amountRefundable, max);
  }
}

// NOTE: named ...Component to avoid colliding with the internal
// `SapRefund` EmberObject class defined above.
export default class SapRefundComponent extends Component {
  @service
  fetch;

  @service
  finance;

  @service
  toaster;

  PaymentMethod = PaymentMethod;

  // community specifies whether community payments are being refunded.
  community = false;

  // transactions specifies the Transaction(s) to be refunded.
  transactions = null;

  // onClose specifies the action to perform when the dialog is closed.
  // A single boolean parameter will be passed, hinting whether to refresh the
  // data being displayed on the page (`true` if a refund was processed).
  onClose() {}

  @tracked
  refundInProgress = false;

  refunds = [];

  refundsProcessed = 0;

  @tracked
  refundsFailed = 0;

  @computed('accounts.@each.caregiver_ids')
  get potentialPayees() {
    if (this.community) {
      // TODO: dummy person for community payer?
      return [];
    }
    return uniq(
      compact(
        this.accounts.reduce((refundees, account) => {
          // resolve the async hasMany relationship upfront.
          refundees.push(...resolved(account.caregivers));
          return refundees;
        }, []),
      ),
    );
  }

  @tracked
  payee = null;

  @tracked
  payeeFirstName = '';

  @tracked
  payeeLastName = '';

  @tracked
  payeeEmail = '';

  @action
  setPayeeEmail(value) {
    this.payeeEmail = value;
  }

  @tracked
  payeeAddress1 = '';

  @action
  setPayeeAddress1(value) {
    this.payeeAddress1 = value;
  }

  @tracked
  payeeAddress2 = '';

  @action
  setPayeeAddress2(value) {
    this.payeeAddress2 = value;
  }

  @tracked
  payeeAddress3 = '';

  @action
  setPayeeAddress3(value) {
    this.payeeAddress3 = value;
  }

  @tracked
  payeeSuburbCity = '';

  @action
  setPayeeSuburbCity(value) {
    this.payeeSuburbCity = value;
  }

  @tracked
  payeeState = '';

  @action
  setPayeeState(value) {
    this.payeeState = value;
  }

  @tracked
  payeePostcode = '';

  @action
  setPayeePostcode(value) {
    this.payeePostcode = value;
  }

  @tracked
  bsb = '';

  @action
  setBsb(value) {
    this.bsb = value;
  }

  @tracked
  bankAccountNo = '';

  @action
  setBankAccountNo(value) {
    this.bankAccountNo = value;
  }

  @tracked
  comment = '';

  @action
  setComment(value) {
    this.comment = value;
  }

  bsbValidations = Object.freeze([
    {
      message: 'BSB must be in the form XXX-XXX, where X is a digit',
      validate: (inputValue) => /^\d{3}-\d{3}$/.test(inputValue),
    },
  ]);

  bankAccountNoValidations = Object.freeze([
    {
      message: 'Bank account number must be 1-9 digits and cannot be all zeros',
      validate: (inputValue) => /^(?!0+$)[0-9]{1,9}$/.test(inputValue),
    },
  ]);

  postcodeValidations = Object.freeze([
    {
      message: 'Post code must be valid',
      validate: (inputValue) =>
        /^((0[289][0-9]{2})|([1345689][0-9]{3})|(2[0-8][0-9]{2})|(290[0-9])|(291[0-4])|(7[0-4][0-9]{2})|(7[8-9][0-9]{2}))$/.test(
          inputValue,
        ),
    },
  ]);

  sapEmailValidations = Object.freeze([
    {
      message: 'Email address appears to be invalid',
      validate: (inputValue) =>
        isEmpty(inputValue) ||
        /^(?=^.{6,128}$)[\w\-.]{1,64}@([\w-]+\.)+[\w-]{2,}$/.test(inputValue),
    },
  ]);

  states = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

  @computed('refunds.@each.method')
  get hasEftRefund() {
    return this.refunds.some(
      (refund) => refund.method === PaymentMethod.INTERNET,
    );
  }

  @computed('refunds.@each.{method,amount}')
  get eftTotal() {
    return filterBy(this.refunds, 'method', PaymentMethod.INTERNET).reduce(
      (total, refund) => {
        return total + parseFloat(refund.amount);
      },
      0,
    );
  }

  @computed('refunds.@each.{method,amount}')
  get eftposTotal() {
    return filterBy(this.refunds, 'method', PaymentMethod.EFTPOS).reduce(
      (total, refund) => {
        return total + parseFloat(refund.amount);
      },
      0,
    );
  }

  @computed('refunds.@each.{method,amount}')
  get onlineTotal() {
    return filterBy(this.refunds, 'method', PaymentMethod.ONLINE).reduce(
      (total, refund) => {
        return total + parseFloat(refund.amount);
      },
      0,
    );
  }

  @computed('refunds.@each.amount')
  get refundTotal() {
    return this.refunds.reduce((total, refund) => {
      return total + parseFloat(refund.amount);
    }, 0);
  }

  @computed('refunds.@each.{refundType,refundFrom,amount}')
  get itemTotals() {
    return filterBy(this.refunds, 'refundType', 'item').reduce(
      (totals, refund) => {
        let id = refund.get('refundFrom.id');
        if (isEmpty(id)) {
          return totals;
        }
        if (isEmpty(totals[id])) {
          totals[id] = 0;
        }
        totals[id] += parseFloat(refund.amount);
        return totals;
      },
      {},
    );
  }

  @computed('refunds.@each.{refundType,refundFrom,amount}')
  get accountTotals() {
    return filterBy(this.refunds, 'refundType', 'account').reduce(
      (totals, refund) => {
        let id = refund.get('refundFrom.id');
        if (isEmpty(id)) {
          return totals;
        }
        if (isEmpty(totals[id])) {
          totals[id] = 0;
        }
        totals[id] += parseFloat(refund.amount);
        return totals;
      },
      {},
    );
  }

  @computed(
    'refunds.@each.{refundType,refundFrom}',
    'itemTotals',
    'accountTotals',
  )
  get refundValidations() {
    return this.refunds.reduce((validations, refund) => {
      if (isEmpty(refund.get('refundFrom.id'))) {
        // No item/account selected yet; skip.
        return validations;
      }
      let key = `${refund.refundType}${refund.get('refundFrom.id')}`;
      if (!isEmpty(validations[key])) {
        // Already in the map; skip.
        return validations;
      }
      // Not yet in the map; add it.
      let name = '';
      let maxRefund = 0;
      let total = 0;
      if (refund.refundType === 'account') {
        name = `account (${refund.get('refundFrom.person.fullName')})`;
        maxRefund = refund.get('refundFrom.balance');
        total = this.accountTotals[refund.get('refundFrom.id')];
      } else {
        name = `line item (${refund.get('refundFrom.name')})`;
        maxRefund = refund.get('refundFrom.credit');
        total = this.itemTotals[refund.get('refundFrom.id')];
      }

      if (maxRefund <= 0) {
        validations[key] = [
          {
            message: `The ${name} has no refundable balance.  Please select a different account or line item.`,
            validate: () => false,
          },
        ];
      } else {
        validations[key] = [
          {
            message: `Total refund for ${name} cannot exceed ${maxRefund.toFixed(
              2,
            )}`,
            validate: () => total <= maxRefund,
          },
        ];
      }

      return validations;
    }, {});
  }

  @computed('refunds.@each.{method,amount,transaction}')
  get eftposRefundsByPayee() {
    return filterBy(this.refunds, 'method', PaymentMethod.EFTPOS).reduce(
      (totals, refund) => {
        let payee = refund.get('transaction.paidBy').trim();
        if (isEmpty(totals[payee])) {
          totals[payee] = 0;
        }
        totals[payee] += parseFloat(refund.amount);
        return totals;
      },
      {},
    );
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    let refunds = this.transactions.reduce((refunds, transaction) => {
      let method = this.getRefundMethod(transaction);
      if (transaction.piaAmount > 0) {
        refunds.push(
          SapRefund.create({
            transaction,
            method,
            amount: transaction.piaAmountRefundable.toFixed(2),
            amountRefundable: transaction.piaAmountRefundable,
            refundType: 'account',
            refundFrom: transaction.account,
          }),
        );
      }
      transaction.childTransactions.forEach((txn) => {
        let refund = SapRefund.create({
          transaction: txn,
          method,
          amount: txn.amountRefundable.toFixed(2),
          amountRefundable: txn.amountRefundable,
          refundType: 'item',
          refundFrom: txn.lineItem,
        });
        if (isEmpty(txn.line_item_id)) {
          // Apportionment to a sibling's account.
          refund.setProperties({
            refundType: 'account',
            refundFrom: txn.account,
          });
        }
        refunds.push(refund);
      });
      return refunds;
    }, []);
    this.set('refunds', refunds);
  }

  // getRefundMethod determines the refund method for a given transaction.
  getRefundMethod(transaction) {
    // INTERNET is used to signify an EFT refund via SAP, as the refund will
    // be refunded to the payee's bank account by DoE.
    let method = PaymentMethod.INTERNET;
    if (isEmpty(transaction.reference)) {
      // Migrated transactions have a reference, and can only be refunded via
      // EFT/SAP, per NSW DoE.  Otherwise, EFTPOS and Online (CPP) payments
      // must be refunded via the original method.
      switch (transaction.method) {
        case PaymentMethod.EFTPOS:
          method = PaymentMethod.EFTPOS;
          break;
        case PaymentMethod.ONLINE:
          // Only switch to CPP refund if we have a CPP transaction.
          if (transaction.nswCppTransactionId) {
            method = PaymentMethod.ONLINE;
          }
      }
    }
    return method;
  }

  getEftRefundDetails() {
    return this.getProperties(
      'payeeFirstName',
      'payeeLastName',
      'payeeEmail',
      'payeeAddress1',
      'payeeAddress2',
      'payeeAddress3',
      'payeeSuburbCity',
      'payeeState',
      'payeePostcode',
      'bsb',
      'bankAccountNo',
    );
  }

  @action
  setRefundType(refund, refundType) {
    let props = {
      refundType,
      refundFrom: null,
    };
    if (refundType === 'account') {
      props.refundFrom = refund.get('transaction.account');
    } else if (!isEmpty(refund.get('transaction.line_item_id'))) {
      props.refundFrom = refund.get('transaction.lineItem');
    }
    refund.setProperties(props);
  }

  @action
  selectPayee(person) {
    this.payee = person;
    let props = {
      payeeFirstName: '',
      payeeLastName: '',
      payeeEmail: '',
      payeeAddress1: '',
      payeeAddress2: '',
      payeeAddress3: '',
      payeeSuburbCity: '',
      payeeState: '',
      payeePostcode: '',
    };
    if (!isEmpty(person)) {
      props = {
        payeeFirstName: person.firstName,
        payeeLastName: person.lastName,
        payeeEmail: person.get('fields.core:emailAddress.value'),
        payeeAddress1: person.get('fields.core:address1.value'),
        payeeAddress2: person.get('fields.core:address2.value'),
        payeeAddress3: person.get('fields.core:address3.value'),
        payeeSuburbCity: person.get('fields.core:address4.value'),
        payeeState: '',
        payeePostcode: person.get('fields.core:postcode.value'),
      };
    }
    this.setProperties(props);
  }

  @action
  setFirstName(name) {
    this.payeeFirstName = name;
    this.payee = null;
  }

  @action
  setLastName(name) {
    this.payeeLastName = name;
    this.payee = null;
  }

  @action
  remove(refund) {
    this.set(
      'refunds',
      this.refunds.filter((r) => r !== refund),
    );
  }

  @action
  refund() {
    this.refundInProgress = true;
    this.refundsProcess = 0;
    this.refundsFailed = 0;

    let baseUrl = `${config.APP.apiHost}/finance/${config.APP.apiPath}`;
    let accountUrl = `${baseUrl}/accounts/`;
    let itemUrl = `${baseUrl}/lineitems/`;

    // Filter out any zero-value refunds, since they don't need to be done.
    let refunds = this.refunds.filter((refund) => refund.amount > 0);

    // Perform refunds one at a time.
    let promises = refunds.reduce((chain, refund) => {
      return chain.then(() => {
        let url = itemUrl;
        let fromId = parseInt(refund.get('refundFrom.id'), 10);

        let data = {
          comment: this.comment,
          date: moment().format('YYYY-MM-DD'),
          amount: Math.round(refund.amount * 100),
          method: refund.method,
          transactionId: parseInt(refund.get('transaction.id'), 10),
          // Default person ID/name from the original transaction.
          personId: refund.get('transaction.person.id'),
          // Use personName - NOT paidBy - so that we get an ID _or_ a name.
          personName: refund.get('transaction.personName'),
        };

        if (refund.refundType === 'account') {
          url = accountUrl;
          data.accountId = fromId;
        } else {
          data.lineItemId = fromId;
        }

        switch (refund.method) {
          case PaymentMethod.INTERNET:
            // EFT refund.  Add the extra details.
            data.nswEftRefund = this.getEftRefundDetails();
            if (!isEmpty(this.payee)) {
              data.personId = this.get('payee.id');
              delete data.personName;
            } else {
              data.personName =
                `${this.payeeFirstName} ${this.payeeLastName}`.trim();
              delete data.personId;
            }
            break;

          case PaymentMethod.EFTPOS:
            // EFTPOS refunds are associated with a Till (Session).
            data.tillId = parseInt(this.get('finance.activeTill.id'), 10);
            break;
        }

        url = `${url}${fromId}/:refund`;
        let options = {
          data: JSON.stringify(data),
        };
        return this.fetch
          .post(url, options)
          .then(() => this.incrementProperty('refundsProcessed'))
          .catch(() => this.incrementProperty('refundsFailed'));
      });
    }, Promise.resolve());

    promises.finally(() => {
      if (this.refundsFailed > 0) {
        if (this.refundsProcessed === 0) {
          this.toaster.error('Refund failed.  Please refresh and try again.');
        } else {
          this.toaster.error(
            `Some refunds weren't able to be processed.  Please refresh, review the results, and try again.`,
          );
        }
      } else {
        this.toaster.success('Refund(s) processed successfully.');
      }

      // We're done.  Close the dialog.
      this.onClose(true);
    });
  }

  @action
  close() {
    this.onClose(false);
  }
}
